import { useEffect, useMemo, useState } from 'react';
import { useFlag } from '@unleash/proxy-client-react';

/**
 * PRD-1699: role capabilities. While `role-capabilities` is on, each surface below
 * shows only when the signed-in user's role holds its capability, and a flag that
 * also gates the surface still has to be on (E16). A capability counts only when the
 * profile says exactly `true`, so a missing key or a failed profile hides it (E15).
 *
 * The `me.features` keys are proposed until resapi ships them: rename them here only.
 */
export const CAPABILITIES = {
  insights: 'can_access_insights_page',
  datasets: 'can_view_datasets',
  connections: 'can_view_connections',
  assessments: 'can_access_assessments',
  library: 'can_access_library',
  activities: 'can_access_activities',
  notifications: 'can_access_notifications',
  updateProfile: 'can_update_own_profile',
  editOrganization: 'can_edit_organization',
  manageUsers: 'can_manage_users',
  manageSso: 'can_configure_sso',
  managePermissions: 'can_manage_permissions',
  assessmentSetup: 'can_manage_assessment_setup',
};

// The two Assessments capabilities are Navy Phase 0 only and also need their own flag.
const NAVY_ONLY = ['assessments', 'assessmentSetup'];

// Settings shows when any of these is held (R5); Data Sources likewise (R6).
export const SETTINGS_SECTIONS = ['editOrganization', 'manageUsers', 'manageSso', 'managePermissions', 'assessmentSetup'];
export const DATA_SOURCES_TABS = ['datasets', 'connections', 'assessments'];

export function capabilityCheck(features, { navyEnabled = false } = {}) {
  const has = (name) => {
    if (NAVY_ONLY.includes(name) && !navyEnabled) return false;
    return features?.[CAPABILITIES[name]] === true;
  };
  return { has, any: (names) => names.some(has) };
}

/**
 * Whether the role may open a page, or undefined for a page capabilities don't
 * cover. The flag that also gates a page (E16) is the caller's to check: a page
 * whose flag is off is gone for everyone, not refused to this role. Push to BI has
 * no capability but keeps the Sidebar's own rule, so nobody is sent to a page
 * their nav doesn't show.
 */
export function capabilityPageAccess(pageId, caps, features) {
  switch (pageId) {
    case 'insights': return caps.has('insights');
    case 'data-sources': return caps.any(DATA_SOURCES_TABS);
    case 'library': return caps.has('library');
    case 'activity-log': return caps.has('activities');
    case 'push-to-bi': return features?.can_access_push_to_bi !== false;
    case 'settings': return caps.any(SETTINGS_SECTIONS);
    default: return undefined;
  }
}

/**
 * The tab to show among `tabs` (each `{ id, allowed }`): with
 * `role-capabilities` off every tab shows; on, only the allowed ones. The page
 * opens on the first shown, and if the open tab stops being allowed it moves
 * to the first shown, or to null when none is. Settings and Data Sources share it.
 * The move shows in the same render, so a tab no longer allowed never paints.
 */
export function useAllowedTabs(caps, tabs) {
  const shown = tabs.filter((tab) => !caps.enabled || tab.allowed);
  const first = shown[0]?.id ?? null;
  const [active, setActive] = useState(first);
  const activeShown = shown.some((tab) => tab.id === active);
  useEffect(() => {
    if (!activeShown) setActive(first);
  }, [activeShown, first]);
  return { shown, active: activeShown ? active : first, setActive };
}

/** `enabled` is false while `role-capabilities` is off: callers keep today's rules. */
export function useCapabilities(features) {
  const enabled = useFlag('role-capabilities');
  const navyEnabled = useFlag('navy-assessment-permissions');
  return useMemo(
    () => ({ enabled, ...capabilityCheck(features, { navyEnabled }) }),
    [enabled, navyEnabled, features],
  );
}
